(() => {
  'use strict';

  const q = (selector, root = document) => root.querySelector(selector);
  const qa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const money = (cents, currency = 'GBP') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `£${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  async function refreshCartCount() {
    try {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      qa('[data-rt-cart-count], [data-cart-count]').forEach((node) => {
        node.textContent = cart.item_count;
        node.classList.toggle('is-empty', cart.item_count === 0);
      });
    } catch (_) {}
  }

  function swapProductImage(section, variant) {
    const src = variant?.featured_image?.src || variant?.featured_image?.url;
    if (!src) return;
    const main = q('[data-gallery-main-image]', section);
    if (!main) return;

    main.classList.add('is-switching');
    const preload = new Image();
    preload.onload = () => {
      main.removeAttribute('srcset');
      main.removeAttribute('sizes');
      main.src = src;
      if (variant.featured_image?.alt) main.alt = variant.featured_image.alt;
      requestAnimationFrame(() => main.classList.remove('is-switching'));
    };
    preload.onerror = () => main.classList.remove('is-switching');
    preload.src = src;

    qa('[data-gallery-thumb]', section).forEach((thumb) => {
      const thumbSrc = thumb.dataset.mediaSrc || '';
      const active = thumbSrc && (thumbSrc.includes(src.split('?')[0]) || src.includes(thumbSrc.split('?')[0]));
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function initProductAvailability(root = document) {
    qa('[data-product-section]:not([data-rt-enhanced-product])', root).forEach((section) => {
      section.dataset.rtEnhancedProduct = 'true';
      const jsonNode = q('[data-product-variants]', section);
      const select = q('select[data-variant-select]', section);
      if (!jsonNode || !select) return;

      let variants = [];
      try { variants = JSON.parse(jsonNode.textContent || '[]'); } catch (_) { return; }

      const price = q('[data-rt-variant-price]', section);
      const availability = q('[data-product-availability]', section);
      const availabilityText = q('[data-product-availability-text]', section);
      const controls = q('[data-purchase-controls]', section);
      const dynamic = q('[data-purchase-dynamic]', section);
      const notify = q('[data-notify-panel]', section);
      const notifyVariant = q('[data-notify-variant]', section);

      const update = () => {
        const variant = variants.find((item) => String(item.id) === String(select.value));
        if (!variant) return;

        if (price) {
          const compare = Number(variant.compare_at_price || 0);
          price.innerHTML = `<span class="rt-price__current">${money(variant.price, window.Shopify?.currency?.active || 'GBP')}</span>${compare > Number(variant.price) ? `<s class="rt-price__compare">${money(compare, window.Shopify?.currency?.active || 'GBP')}</s>` : ''}`;
        }

        availability?.classList.toggle('is-unavailable', !variant.available);
        if (availabilityText) availabilityText.textContent = variant.available ? 'In stock and ready to order' : 'Currently unavailable';
        if (controls) controls.hidden = !variant.available;
        if (dynamic) dynamic.hidden = !variant.available;
        if (notify) notify.hidden = variant.available;
        if (notifyVariant) notifyVariant.value = variant.title || 'Default configuration';
        swapProductImage(section, variant);
      };

      select.addEventListener('change', update);
      update();
    });
  }

  function initLaptopBuilder(root = document) {
    qa('[data-rt-builder]:not([data-builder-ready])', root).forEach((builder) => {
      builder.dataset.builderReady = 'true';
      const variantsNode = q('[data-builder-variants]', builder);
      if (!variantsNode) return;

      let variants = [];
      try { variants = JSON.parse(variantsNode.textContent || '[]'); } catch (_) { return; }
      if (!variants.length) return;

      const optionCount = variants[0].options?.length || 0;
      const selections = new Array(optionCount).fill(null);
      const currency = builder.dataset.currency || window.Shopify?.currency?.active || 'GBP';
      let usecase = '';
      let selectedVariant = null;

      const totalNode = q('[data-builder-total]', builder);
      const availabilityNode = q('[data-builder-availability]', builder);
      const addButton = q('[data-builder-add]', builder);
      const messageNode = q('[data-builder-message]', builder);
      const codeNode = q('[data-builder-code]', builder);
      const usecaseSummary = q('[data-builder-summary-usecase]', builder);
      const buildReference = `RC-${Math.random().toString(36).slice(2, 6).toUpperCase()}-${Math.floor(1000 + Math.random() * 9000)}`;
      if (codeNode) codeNode.textContent = buildReference;

      const matchesPartial = (variant, proposed, ignoreIndex = -1) => {
        return proposed.every((value, index) => index === ignoreIndex || !value || String(variant.options?.[index]) === String(value));
      };

      const exactVariant = () => {
        if (!selections.length || !selections.every(Boolean)) return null;
        return variants.find((variant) => selections.every((value, index) => String(variant.options?.[index]) === String(value))) || null;
      };

      const updateOptionStates = () => {
        qa('[data-builder-option]', builder).forEach((button) => {
          const index = Number(button.dataset.optionIndex);
          const value = button.dataset.value || '';
          const proposed = selections.slice();
          proposed[index] = value;
          const configured = variants.filter((variant) => matchesPartial(variant, proposed));
          const anyAvailable = configured.some((variant) => variant.available);
          const impossible = configured.length === 0;

          button.disabled = impossible;
          button.classList.toggle('is-impossible', impossible);
          button.classList.toggle('is-sold-out', !impossible && !anyAvailable);
          button.setAttribute('aria-disabled', impossible ? 'true' : 'false');

          const status = q('em', button);
          if (status) {
            if (impossible) status.innerHTML = '<i class="rt-stock-dot rt-stock-dot--limited"></i>Not available with current choices';
            else if (anyAvailable) status.innerHTML = '<i class="rt-stock-dot"></i>Available combination';
            else status.innerHTML = '<i class="rt-stock-dot rt-stock-dot--limited"></i>Configured but sold out';
          }
        });
      };

      const clearInvalidSelections = (changedIndex) => {
        let changed = true;
        while (changed) {
          changed = false;
          selections.forEach((value, index) => {
            if (!value || index === changedIndex) return;
            const possible = variants.some((variant) => matchesPartial(variant, selections));
            if (!possible) {
              selections[index] = null;
              qa(`[data-builder-option][data-option-index="${index}"]`, builder).forEach((item) => {
                item.classList.remove('is-selected');
                item.setAttribute('aria-pressed', 'false');
              });
              changed = true;
            }
          });
        }
      };

      const update = () => {
        qa('[data-builder-summary-option]', builder).forEach((node) => {
          const index = Number(node.dataset.builderSummaryOption);
          node.textContent = selections[index] || 'Choose above';
        });
        if (usecaseSummary) usecaseSummary.textContent = usecase || 'Not selected';

        updateOptionStates();
        selectedVariant = exactVariant();
        const complete = selections.length > 0 && selections.every(Boolean);

        if (!complete) {
          if (totalNode) totalNode.textContent = '—';
          if (availabilityNode) {
            availabilityNode.textContent = 'Select all required specification options to see the exact live configuration.';
            availabilityNode.classList.remove('is-available', 'is-unavailable');
          }
          if (addButton) {
            addButton.disabled = true;
            addButton.textContent = 'Complete your specification';
          }
          return;
        }

        if (!selectedVariant) {
          if (totalNode) totalNode.textContent = '—';
          if (availabilityNode) {
            availabilityNode.textContent = 'That exact combination is not currently offered. Choose another option.';
            availabilityNode.classList.add('is-unavailable');
            availabilityNode.classList.remove('is-available');
          }
          if (addButton) {
            addButton.disabled = true;
            addButton.textContent = 'Combination unavailable';
          }
          return;
        }

        if (totalNode) totalNode.textContent = money(selectedVariant.price, currency);
        if (availabilityNode) {
          availabilityNode.textContent = selectedVariant.available
            ? 'Exact configuration available — ready to add to cart.'
            : 'This exact configuration is currently sold out.';
          availabilityNode.classList.toggle('is-available', !!selectedVariant.available);
          availabilityNode.classList.toggle('is-unavailable', !selectedVariant.available);
        }
        if (addButton) {
          addButton.disabled = !selectedVariant.available;
          addButton.textContent = selectedVariant.available
            ? `Add configuration to cart — ${money(selectedVariant.price, currency)}`
            : 'Configuration sold out';
        }
      };

      qa('[data-builder-usecase]', builder).forEach((button) => {
        button.addEventListener('click', () => {
          qa('[data-builder-usecase]', builder).forEach((item) => {
            item.classList.remove('is-selected');
            item.setAttribute('aria-pressed', 'false');
          });
          button.classList.add('is-selected');
          button.setAttribute('aria-pressed', 'true');
          usecase = button.dataset.builderUsecase || '';
          update();
        });
      });

      qa('[data-builder-option]', builder).forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          const index = Number(button.dataset.optionIndex);
          qa(`[data-builder-option][data-option-index="${index}"]`, builder).forEach((item) => {
            item.classList.remove('is-selected');
            item.setAttribute('aria-pressed', 'false');
          });
          button.classList.add('is-selected');
          button.setAttribute('aria-pressed', 'true');
          selections[index] = button.dataset.value || null;
          clearInvalidSelections(index);
          update();
        });
      });

      addButton?.addEventListener('click', async () => {
        if (!selectedVariant?.available) return;
        addButton.disabled = true;
        const original = addButton.textContent;
        addButton.textContent = 'Adding your configuration…';
        if (messageNode) messageNode.textContent = '';

        const properties = { 'Build reference': buildReference };
        if (usecase) properties['Use case'] = usecase;
        qa('[data-builder-property]', builder).forEach((field) => {
          const key = field.dataset.builderProperty;
          if (!key) return;
          if (field.type === 'checkbox') {
            if (field.checked) properties[key] = field.value || 'Yes';
          } else if (field.value?.trim()) {
            properties[key] = field.value.trim();
          }
        });

        try {
          const rootUrl = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${rootUrl}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: selectedVariant.id, quantity: 1, properties }] })
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || 'Unable to add this configuration.');
          await refreshCartCount();
          if (messageNode) messageNode.textContent = 'Configuration added. Opening your cart…';
          window.setTimeout(() => { window.location.href = builder.dataset.cartUrl || `${rootUrl}cart`; }, 450);
        } catch (error) {
          if (messageNode) messageNode.textContent = error.message || 'Something went wrong. Please try again.';
          addButton.disabled = false;
          addButton.textContent = original;
        }
      });

      update();
    });
  }

  function initWarrantyModals(root = document) {
    qa('[data-warranty-modal]:not([data-warranty-ready])', root).forEach((modal) => {
      modal.dataset.warrantyReady = 'true';
      const section = modal.closest('[data-product-section]') || document;
      const dialog = q('.rt-warranty-modal__dialog', modal);
      const closers = qa('[data-warranty-close]', modal);
      const openers = qa(`[data-warranty-open][aria-controls="${modal.id}"]`, section);
      let lastFocused = null;

      const close = () => {
        modal.setAttribute('aria-hidden', 'true');
        modal.hidden = true;
        document.body.classList.remove('rt-locked');
        lastFocused?.focus();
      };

      const open = (event) => {
        lastFocused = event?.currentTarget || document.activeElement;
        modal.hidden = false;
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('rt-locked');
        window.setTimeout(() => dialog?.focus(), 20);
      };

      openers.forEach((button) => button.addEventListener('click', open));
      closers.forEach((button) => button.addEventListener('click', close));
      modal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
      });
    });
  }

  function init(root = document) {
    initProductAvailability(root);
    initLaptopBuilder(root);
    initWarrantyModals(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
